/**
 * Drug Data Service
 *
 * Strategy:
 *   1. Try to fetch from the backend API first.
 *   2. If the request fails (network error, server down, timeout), fall back
 *      to the local newdata.json that is bundled with the app.
 *
 * All consuming components/pages use the same four exported helpers so that
 * swapping the data source requires no further changes in those files.
 */

import axios from 'axios';
import localData from '../data/newdata.json';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';
const REQUEST_TIMEOUT = 8000; // ms — fall back quickly if server is down

// ── Restricted drugs — never shown in search, browse, or detail views ──────
const RESTRICTED_DRUGS = new Set([
    'darzalex',
    'harvoni',
    'orkambi',
    'sovaldi',
    'tecfidera',
]);

const isRestricted = (drug) => {
    const brand = drug?.drug_metadata?.brand_name?.toLowerCase() || '';
    const generic = drug?.drug_metadata?.generic_name?.toLowerCase() || '';
    return [...RESTRICTED_DRUGS].some(r => brand.includes(r) || generic.includes(r));
};

// ── In-memory cache so we only fetch once per session ──────────────────────
let _cachedAll = null;          // full array
let _fetchPromise = null;       // ongoing fetch (deduplicate concurrent callers)

/**
 * Fetch the full drug array.
 * Returns the backend payload if available, otherwise local JSON.
 */
async function fetchAll() {
    // Return cached data instantly
    if (_cachedAll) return _cachedAll;

    // Deduplicate concurrent calls — all await the same promise
    if (_fetchPromise) return _fetchPromise;

    _fetchPromise = (async () => {
        try {
            const res = await axios.get(`${API_BASE}/drugs`, {
                timeout: REQUEST_TIMEOUT,
            });
            if (res.data?.success && Array.isArray(res.data.data)) {
                _cachedAll = res.data.data.filter(d => !isRestricted(d));
                console.info(`[DrugDataService] ✅ Loaded ${_cachedAll.length} drugs from backend`);
                return _cachedAll;
            }
            throw new Error('Unexpected backend response shape');
        } catch (err) {
            console.warn(`[DrugDataService] ⚠️ Backend unavailable — using local JSON fallback. Reason: ${err.message}`);
            _cachedAll = localData.filter(d => !isRestricted(d));
            return _cachedAll;
        } finally {
            _fetchPromise = null;
        }
    })();

    return _fetchPromise;
}

// ── Public API ──────────────────────────────────────────────────────────────

/**
 * getAllDrugs()
 * Returns the full array of drug objects (same shape as newdata.json).
 */
export async function getAllDrugs() {
    return fetchAll();
}

/**
 * findDrugByName(name)
 * Case-insensitive, partial-match search on brand_name and generic_name.
 * Tries the /api/drugs/:name endpoint first; falls back to local search.
 */
export async function findDrugByName(name) {
    if (!name) return null;
    const searchName = name.toLowerCase().trim();

    // Block restricted drugs regardless of source
    if ([...RESTRICTED_DRUGS].some(r => searchName.includes(r) || r.includes(searchName))) return null;

    try {
        const res = await axios.get(`${API_BASE}/drugs/${encodeURIComponent(searchName)}`, {
            timeout: REQUEST_TIMEOUT,
        });
        if (res.data?.success && res.data.data) {
            const drug = res.data.data;
            if (isRestricted(drug)) return null;
            return drug;
        }
    } catch (err) {
        if (err.response?.status !== 404) {
            console.warn(`[DrugDataService] ⚠️ /drugs/:name failed, searching locally. Reason: ${err.message}`);
        }
    }

    // Local fallback search — fetchAll() already has restricted drugs removed
    const all = await fetchAll();
    return all.find(drug =>
        drug?.drug_metadata?.brand_name?.toLowerCase().includes(searchName) ||
        drug?.drug_metadata?.generic_name?.toLowerCase().includes(searchName) ||
        searchName.includes(drug?.drug_metadata?.brand_name?.toLowerCase()) ||
        searchName.includes(drug?.drug_metadata?.generic_name?.toLowerCase())
    ) || null;
}

/**
 * searchDrugs(query)
 * Returns all drugs whose brand_name or generic_name contains the query string.
 */
export async function searchDrugs(query) {
    if (!query || query.trim().length < 2) return [];
    const q = query.toLowerCase().trim();

    try {
        const res = await axios.get(`${API_BASE}/drugs/search`, {
            params: { q },
            timeout: REQUEST_TIMEOUT,
        });
        if (res.data?.success && Array.isArray(res.data.data)) {
            return res.data.data.filter(d => !isRestricted(d));
        }
    } catch (err) {
        console.warn(`[DrugDataService] ⚠️ /drugs/search failed, filtering locally. Reason: ${err.message}`);
    }

    // Local fallback
    const all = await fetchAll();
    return all.filter(drug =>
        drug?.drug_metadata?.brand_name?.toLowerCase().includes(q) ||
        drug?.drug_metadata?.generic_name?.toLowerCase().includes(q)
    );
}

/**
 * getDrugNames()
 * Returns a sorted array of unique brand names (for autocomplete / browse lists).
 */
export async function getDrugNames() {
    const all = await fetchAll();
    return Array.from(
        new Set(all.map(d => d?.drug_metadata?.brand_name).filter(Boolean))
    ).sort();
}
